import React, { useState, useEffect } from 'react';
import { userService } from '../../../_services';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import Pagination from '../../ui/pagination';
import { Loader2, Search, ArrowLeft, CheckCircle, XCircle, Clock } from 'lucide-react';

/**
 * Wise & W-9 Report
 * Modernized admin report for viewing Wise account and W-9 tax status
 */
function WiseW9Report({ hideWiseW9Section }) {
    const [reportList, setReportList] = useState([]);
    const [reportListTemp, setReportListTemp] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(false);

    // Fetch reports on mount
    useEffect(() => {
        const usermetaData = window.atob(localStorage.getItem('ajs_user_id'));
        const userId = usermetaData.split(':')[0];
        const userName = usermetaData.split(':')[1];
        fetchReports(userId, userName);
    }, []);

    const fetchReports = async (userId, userName) => {
        setLoading(true);
        try {
            if (userService.GetUserFinInfo) {
                const apidata = await userService.GetUserFinInfo(userId, userName);

                if (apidata && apidata.length > 0) {
                    setReportList(apidata);
                    setReportListTemp(apidata);
                } else {
                    setReportList([]);
                    setReportListTemp([]);
                }
            } else {
                console.warn('GetUserFinInfo API not available');
                setReportList([]);
                setReportListTemp([]);
            }
        } catch (err) {
            console.error('Error fetching Wise & W-9 reports:', err);
            setReportList([]);
            setReportListTemp([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle search filter
    const handleSearch = (value) => {
        setSearchText(value);

        if (!value || value.length === 0) {
            setReportList(reportListTemp);
            return;
        }

        const searchLower = value.toLowerCase();
        const filtered = reportListTemp.filter(report => {
            return (
                (report.email && report.email.toLowerCase().includes(searchLower)) ||
                (report.fullName && report.fullName.toLowerCase().includes(searchLower)) ||
                (report.userName && report.userName.toLowerCase().includes(searchLower)) ||
                (report.phone && report.phone.toLowerCase().includes(searchLower)) ||
                (report.userType && report.userType.toLowerCase().includes(searchLower)) ||
                (report.wiseStatus && report.wiseStatus.toLowerCase().includes(searchLower)) ||
                (report.w9Status && report.w9Status.toLowerCase().includes(searchLower))
            );
        });

        setReportList(filtered.length > 0 ? filtered : []);
    };

    // Get status badge with icon
    const getStatusBadge = (status) => {
        if (!status) return <Badge variant="secondary">Unknown</Badge>;

        const statusLower = status.toLowerCase();

        if (statusLower.includes('complete') || statusLower.includes('active') || statusLower.includes('verified')) {
            return (
                <Badge variant="default" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    {status}
                </Badge>
            );
        }

        if (statusLower.includes('pending') || statusLower.includes('in progress')) {
            return (
                <Badge variant="secondary" className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {status}
                </Badge>
            );
        }

        if (statusLower.includes('inactive') || statusLower.includes('not') || statusLower.includes('failed')) {
            return (
                <Badge variant="destructive" className="flex items-center gap-1">
                    <XCircle className="h-3 w-3" />
                    {status}
                </Badge>
            );
        }

        return <Badge variant="secondary">{status}</Badge>;
    };

    return (
        <div className="w-full space-y-4">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={hideWiseW9Section}
                    className="text-brand-dark hover:text-brand-accent"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <h2 className="text-2xl font-semibold text-brand-dark">Wise & W-9 Report</h2>
                {loading && <Loader2 className="h-5 w-5 animate-spin text-brand-accent" />}
            </div>

            {/* Search Filter */}
            <Card>
                <CardContent className="pt-6">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-dark/50" />
                        <Input
                            type="text"
                            placeholder="Search by name, email, phone, or status..."
                            value={searchText}
                            onChange={(e) => handleSearch(e.target.value)}
                            disabled={loading}
                            className="pl-9"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Report Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-brand-light-1">
                                    <TableHead className="text-brand-dark font-semibold">User Name</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Full Name</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Email</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Phone</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">User Type</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">Wise Status</TableHead>
                                    <TableHead className="text-brand-dark font-semibold">W-9 Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8">
                                            <div className="flex items-center justify-center gap-2 text-brand-dark/70">
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Records are loading...
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ) : reportList.length > 0 ? (
                                    <Pagination items={reportList} pageSize={10}>
                                        {(paginatedItems) =>
                                            paginatedItems.map((report, index) => (
                                                <TableRow key={index} className="hover:bg-brand-light-1">
                                                    <TableCell className="text-brand-dark">{report.userName || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">{report.fullName || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">{report.email || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">{report.phone || '-'}</TableCell>
                                                    <TableCell className="text-brand-dark">
                                                        <Badge variant="outline">{report.userType || 'User'}</Badge>
                                                    </TableCell>
                                                    <TableCell>{getStatusBadge(report.wiseStatus)}</TableCell>
                                                    <TableCell>{getStatusBadge(report.w9Status)}</TableCell>
                                                </TableRow>
                                            ))
                                        }
                                    </Pagination>
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-brand-dark/70">
                                            No records found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default WiseW9Report;
